import { useQuery, type UseQueryOptions, type UseQueryResult } from '@tanstack/react-query';
import {
  getUserBoosters,
  type BEUserBooster,
  type GetUserBoostersParams,
  type UserBoostersResponse,
} from '@/lib/network';

export const USER_BOOSTERS_QUERY_KEY_PREFIX = ['boosters', 'user'] as const;

export const getUserBoostersQueryKey = (
  tournamentCalendarId: string | undefined,
  matchdayId: string | undefined,
  gameweekId: string | undefined
) =>
  [
    ...USER_BOOSTERS_QUERY_KEY_PREFIX,
    tournamentCalendarId ?? '',
    matchdayId ?? '',
    gameweekId ?? '',
  ] as const;

type UserBoostersData = {
  boosters: BEUserBooster[];
  currentPeriodId: string | null;
};

export type UseUserBoostersOptions = Omit<
  UseQueryOptions<UserBoostersResponse, Error, UserBoostersData>,
  'queryKey' | 'queryFn' | 'select'
>;

export const useUserBoosters = (
  params: Partial<GetUserBoostersParams>,
  options?: UseUserBoostersOptions
): UseQueryResult<UserBoostersData, Error> => {
  const { enabled = true, ...restOptions } = options ?? {};
  const tournamentCalendarId = params.tournament_calendar_id;
  const matchdayId = params.matchday_id;
  const gameweekId = params.gameweek_id;

  return useQuery<UserBoostersResponse, Error, UserBoostersData>({
    queryKey: getUserBoostersQueryKey(tournamentCalendarId, matchdayId, gameweekId),
    queryFn: async () => {
      if (!tournamentCalendarId) {
        throw new Error('tournament_calendar_id is required');
      }
      if (!matchdayId && !gameweekId) {
        throw new Error('Either matchday_id or gameweek_id is required');
      }
      return getUserBoosters({
        tournament_calendar_id: tournamentCalendarId,
        matchday_id: matchdayId,
        gameweek_id: gameweekId,
      });
    },
    select: response => ({
      boosters: response.data.boosters,
      currentPeriodId:
        response.data.current_gameweek?.id ?? response.data.current_matchday?.id ?? null,
    }),
    enabled: enabled && !!tournamentCalendarId && (!!matchdayId || !!gameweekId),
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    ...restOptions,
  });
};
